import { ArticleNumberInput } from '@/components/ui/article-number-input';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Form, FormControl, FormDescription, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { MultiLanguageInput } from '@/components/ui/multi-language-input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { useCreateGroup } from '@/hooks/use-groups';
import { useCreateProject } from '@/hooks/use-projects';
import { cn, formatCurrency, getLocalizedText } from '@/lib/utils';
import { createGroupSchema, createProjectSchema, type GroupFormData, type ProjectFormData } from '@fame/shared';
import { zodResolver } from '@hookform/resolvers/zod';
import { ArrowLeft, ArrowRight, Check } from 'lucide-react';
import { useState } from 'react';
import { useForm, type Resolver } from 'react-hook-form';
import { Link, useNavigate } from 'react-router-dom';

const steps = [
  { id: 1, title: 'Project', description: 'Financiële instellingen' },
  { id: 2, title: 'Groep', description: 'Eerste reisgroep' },
  { id: 3, title: 'Bevestiging', description: 'Overzicht' },
];

export function NewProjectPage() {
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(1);
  const [projectData, setProjectData] = useState<ProjectFormData | null>(null);
  const [groupData, setGroupData] = useState<GroupFormData | null>(null);

  const createProject = useCreateProject();
  const createGroup = useCreateGroup();

  const projectForm = useForm<ProjectFormData>({
    resolver: zodResolver(createProjectSchema) as Resolver<ProjectFormData>,
    defaultValues: {
      name: { nl: '', fr: '', en: '' },
      depositArticleNumber: '',
      singleRoomArticleNumber: '',
      balanceArticleNumber: '',
      activityArticleNumber: '',
      activityDescription: { nl: '', fr: '', en: '' },
      defaultCostUnit: '',
      depositAmount: '',
      mollieProfile: 'nocc',
      isActive: true,
    },
  });

  const groupForm = useForm<Omit<GroupFormData, 'projectId'>>({
    resolver: zodResolver(createGroupSchema.omit({ projectId: true })) as Resolver<Omit<GroupFormData, 'projectId'>>,
    defaultValues: {
      name: { nl: '', fr: '', en: '' },
      packagePrice: 0,
      singleRoomSupplement: undefined,
      invoiceDescription: undefined,
      depositDescription: undefined,
      invoiceRemark: undefined,
      isActive: true,
    },
  });

  const onProjectSubmit = (data: ProjectFormData) => {
    setProjectData(data);
    setCurrentStep(2);
  };

  const onGroupSubmit = (data: Omit<GroupFormData, 'projectId'>) => {
    setGroupData(data as GroupFormData);
    setCurrentStep(3);
  };

  const handleFinalSubmit = async () => {
    if (!projectData) return;

    try {
      const project = await createProject.mutateAsync(projectData);

      if (groupData) {
        await createGroup.mutateAsync({
          ...groupData,
          projectId: project.id,
        });
      }

      navigate(`/projecten/${project.id}`);
    } catch {
      // Error handling is done in the mutation hooks
    }
  };

  const handleSkipGroup = async () => {
    if (!projectData) return;

    try {
      const project = await createProject.mutateAsync(projectData);
      navigate(`/projecten/${project.id}`);
    } catch {
      // Error handling is done in the mutation hooks
    }
  };

  return (
    <div className="max-w-3xl mx-auto space-y-6">
      <div className="flex items-center gap-4">
        <Button variant="ghost" size="icon" asChild>
          <Link to="/projecten">
            <ArrowLeft className="h-4 w-4" />
          </Link>
        </Button>
        <div>
          <h1 className="text-2xl font-bold">Nieuw Project</h1>
          <p className="text-muted-foreground">Maak een nieuw reisproject aan</p>
        </div>
      </div>

      {/* Stepper */}
      <div className="flex items-center justify-center">
        {steps.map((step, index) => (
          <div key={step.id} className="flex items-center">
            <div
              className={cn(
                'flex items-center justify-center w-10 h-10 rounded-full border-2 font-medium',
                currentStep > step.id
                  ? 'bg-primary border-primary text-primary-foreground'
                  : currentStep === step.id
                    ? 'border-primary text-primary'
                    : 'border-muted text-muted-foreground'
              )}
            >
              {currentStep > step.id ? <Check className="h-5 w-5" /> : step.id}
            </div>
            <div className="ml-3 hidden sm:block">
              <p
                className={cn(
                  'text-sm font-medium',
                  currentStep >= step.id ? 'text-foreground' : 'text-muted-foreground'
                )}
              >
                {step.title}
              </p>
              <p className="text-xs text-muted-foreground">{step.description}</p>
            </div>
            {index < steps.length - 1 && (
              <div className={cn('w-12 h-0.5 mx-4', currentStep > step.id ? 'bg-primary' : 'bg-muted')} />
            )}
          </div>
        ))}
      </div>

      {/* Step 1: Project */}
      {currentStep === 1 && (
        <Card>
          <CardHeader>
            <CardTitle>Project Informatie</CardTitle>
            <CardDescription>Vul de basisinformatie en financiële instellingen in</CardDescription>
          </CardHeader>
          <CardContent>
            <Form {...projectForm}>
              <form onSubmit={projectForm.handleSubmit(onProjectSubmit)} className="space-y-4">
                <FormField
                  control={projectForm.control}
                  name="name"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Projectnaam</FormLabel>
                      <FormControl>
                        <MultiLanguageInput placeholder="bijv. Japan 2026" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <div className="space-y-4">
                  <div>
                    <h3 className="text-sm font-medium">Facturatie</h3>
                    <p className="text-sm text-muted-foreground">
                      Artikelnummers, kostendrager en voorschotinstellingen
                    </p>
                  </div>

                  <div className="grid gap-4 md:grid-cols-2">
                    <FormField
                      control={projectForm.control}
                      name="depositArticleNumber"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Artikelnummer (voorschotfactuur)</FormLabel>
                          <FormControl>
                            <ArticleNumberInput value={field.value} onChange={field.onChange} placeholder="bijv. GT0" />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    <FormField
                      control={projectForm.control}
                      name="depositAmount"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Bedrag voorschot (pp)</FormLabel>
                          <FormControl>
                            <Input type="number" step="0.01" placeholder="0.00" {...field} value={field.value ?? ''} />
                          </FormControl>
                          <FormDescription>Het bedrag voor het voorschot per persoon</FormDescription>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    <FormField
                      control={projectForm.control}
                      name="singleRoomArticleNumber"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Artikelnummer (single kamer)</FormLabel>
                          <FormControl>
                            <ArticleNumberInput value={field.value} onChange={field.onChange} placeholder="bijv. GT1" />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    <FormField
                      control={projectForm.control}
                      name="balanceArticleNumber"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Artikelnummer (saldo factuur)</FormLabel>
                          <FormControl>
                            <ArticleNumberInput value={field.value} onChange={field.onChange} placeholder="bijv. GT2" />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    <FormField
                      control={projectForm.control}
                      name="activityArticleNumber"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Artikelnummer (activiteiten)</FormLabel>
                          <FormControl>
                            <ArticleNumberInput value={field.value} onChange={field.onChange} placeholder="bijv. GT3" />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    <FormField
                      control={projectForm.control}
                      name="activityDescription"
                      render={({ field }) => (
                        <FormItem className="md:col-span-2">
                          <FormLabel>Activiteiten omschrijving (voor factuur)</FormLabel>
                          <FormControl>
                            <MultiLanguageInput placeholder="bijv. Optionele activiteiten" {...field} />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    <FormField
                      control={projectForm.control}
                      name="defaultCostUnit"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Kostendrager</FormLabel>
                          <FormControl>
                            <Input placeholder="Optioneel" {...field} value={field.value ?? ''} />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    <FormField
                      control={projectForm.control}
                      name="mollieProfile"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Mollie betaalprofiel</FormLabel>
                          <Select onValueChange={field.onChange} value={field.value}>
                            <FormControl>
                              <SelectTrigger>
                                <SelectValue />
                              </SelectTrigger>
                            </FormControl>
                            <SelectContent>
                              <SelectItem value="nocc">Mollie - excl. creditcard</SelectItem>
                              <SelectItem value="cc">Mollie - incl. creditcard</SelectItem>
                            </SelectContent>
                          </Select>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </div>
                </div>

                <FormField
                  control={projectForm.control}
                  name="isActive"
                  render={({ field }) => (
                    <FormItem className="flex items-center justify-between rounded-lg border p-4">
                      <div className="space-y-0.5">
                        <FormLabel className="text-base">Actief</FormLabel>
                        <FormDescription>Is dit project momenteel actief?</FormDescription>
                      </div>
                      <FormControl>
                        <Switch checked={field.value} onCheckedChange={field.onChange} />
                      </FormControl>
                    </FormItem>
                  )}
                />

                <div className="flex justify-end">
                  <Button type="submit">
                    Volgende
                    <ArrowRight className="ml-2 h-4 w-4" />
                  </Button>
                </div>
              </form>
            </Form>
          </CardContent>
        </Card>
      )}

      {/* Step 2: Group */}
      {currentStep === 2 && (
        <Card>
          <CardHeader>
            <CardTitle>Eerste Groep</CardTitle>
            <CardDescription>Maak optioneel de eerste reisgroep aan voor dit project</CardDescription>
          </CardHeader>
          <CardContent>
            <Form {...groupForm}>
              <form onSubmit={groupForm.handleSubmit(onGroupSubmit)} className="space-y-4">
                <FormField
                  control={groupForm.control}
                  name="name"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Groepsnaam</FormLabel>
                      <FormControl>
                        <MultiLanguageInput placeholder="bijv. Wave 1" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={groupForm.control}
                  name="packagePrice"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Pakketprijs (pp)</FormLabel>
                      <FormControl>
                        <Input type="number" step="0.01" min="0" placeholder="0.00" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={groupForm.control}
                  name="singleRoomSupplement"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Single kamer supplement</FormLabel>
                      <FormControl>
                        <Input
                          type="number"
                          step="0.01"
                          min="0"
                          placeholder="0,00"
                          {...field}
                          value={field.value ?? ''}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={groupForm.control}
                  name="invoiceDescription"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Factuur Omschrijving</FormLabel>
                      <FormControl>
                        <MultiLanguageInput placeholder="bijv. Symposium Japan" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={groupForm.control}
                  name="depositDescription"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Factuurregel Voorschot Omschrijving</FormLabel>
                      <FormControl>
                        <MultiLanguageInput placeholder="bijv. Voorschot" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={groupForm.control}
                  name="invoiceRemark"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Factuur Opmerking</FormLabel>
                      <FormControl>
                        <MultiLanguageInput placeholder="bijv. 6-14 Nov" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <div className="flex justify-between">
                  <div className="flex gap-2">
                    <Button type="button" variant="outline" onClick={() => setCurrentStep(1)}>
                      <ArrowLeft className="mr-2 h-4 w-4" />
                      Vorige
                    </Button>
                    <Button type="button" variant="ghost" onClick={handleSkipGroup} disabled={createProject.isPending}>
                      Overslaan
                    </Button>
                  </div>
                  <Button type="submit">
                    Volgende
                    <ArrowRight className="ml-2 h-4 w-4" />
                  </Button>
                </div>
              </form>
            </Form>
          </CardContent>
        </Card>
      )}

      {/* Step 3: Confirmation */}
      {currentStep === 3 && (
        <Card>
          <CardHeader>
            <CardTitle>Bevestiging</CardTitle>
            <CardDescription>Controleer de gegevens en maak het project aan</CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            <div>
              <h3 className="font-medium mb-2">Project</h3>
              <div className="bg-muted rounded-lg p-4 space-y-2">
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Naam</span>
                  <span className="font-medium">{getLocalizedText(projectData?.name)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Artikelnummer (voorschot)</span>
                  <span>{projectData?.depositArticleNumber}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Artikelnummer (single kamer)</span>
                  <span>{projectData?.singleRoomArticleNumber}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Artikelnummer (saldo factuur)</span>
                  <span>{projectData?.balanceArticleNumber}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Artikelnummer (activiteiten)</span>
                  <span>{projectData?.activityArticleNumber}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Bedrag voorschot (pp)</span>
                  <span>{projectData?.depositAmount ? formatCurrency(projectData.depositAmount) : '-'}</span>
                </div>
              </div>
            </div>

            {groupData && (
              <div>
                <h3 className="font-medium mb-2">Groep</h3>
                <div className="bg-muted rounded-lg p-4 space-y-2">
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Naam</span>
                    <span className="font-medium">{getLocalizedText(groupData.name)}</span>
                  </div>
                </div>
              </div>
            )}

            <div className="flex justify-between">
              <Button variant="outline" onClick={() => setCurrentStep(2)}>
                <ArrowLeft className="mr-2 h-4 w-4" />
                Vorige
              </Button>
              <Button onClick={handleFinalSubmit} disabled={createProject.isPending || createGroup.isPending}>
                {createProject.isPending || createGroup.isPending ? 'Bezig met aanmaken...' : 'Project Aanmaken'}
                <Check className="ml-2 h-4 w-4" />
              </Button>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
